import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import ReCAPTCHA from "react-google-recaptcha";
import "./verificationScreen.css";
import CustomButton from "../../common/customButton/customButton";
import UniformLoader from "../../common/uniformLoader/uniformLoader";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/ksolactipanel.png";
import { useSharedState } from "../../context/formsContext";
import CustomInput from "../../common/customInput/customInput";
import tick from "../../assets/tickForVerified.png";
import gcseLogo from "../../assets/KSOL GCSE_logo.png";
import { getProperty } from "../../utils/apiResponseHelper";
import {
  trackFreeDownloadSignupComplete,
  trackFreeDownloadSignupStep,
} from "../../utils/analytics";

const VerificationScreen = () => {
  const navigate = useNavigate();
  const inputsRef = useRef([]);
  const [loading, setLoading] = useState(true);

  const [verificationCode, setVerificationCode] = useState({
    one: "",
    two: "",
    three: "",
    four: "",
    five: "",
  });
  const [combinedVerification, setCombinedVerification] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [shake, setShake] = useState(false);
  const [showTick, setShowTick] = useState(false);
  const [resendLoad, setResendLoad] = useState(false);
  const { value, setValue, verified, setVerified, prefix } = useSharedState();
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [sessionCourseId, setSessionCourseId] = useState(null);
  const [isFreeDownloadsRegistration, setIsFreeDownloadsRegistration] = useState(false);

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    // Reset verified state when component mounts to ensure user must enter verification code
    setVerified(false);

    return () => {
      isMounted.current = false;
      setShowCaptcha(false);
    };
  }, [setVerified]);

  useEffect(() => {
    const courseId = localStorage.getItem("gcse");
    if (courseId === "true") {
      setSessionCourseId(true);
    } else {
      setSessionCourseId(false);
    }
  }, []);

  const handleRecaptchaChange = () => {
    if (isMounted.current) {
      resendVerificationCode();
    }
  };

  const focusNextInput = (index) => {
    if (index < inputsRef.current.length - 1) {
      const nextInput = inputsRef.current[index + 1];
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  const focusPrevInput = (index) => {
    if (index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (
      e.key === "Backspace" &&
      verificationCode[Object.keys(verificationCode)[index]] === ""
    ) {
      focusPrevInput(index);
    }
  };

  const handleInputChange = (key, e, index) => {
    const { value } = e.target;

    if (/^[0-9]$/.test(value)) {
      setVerificationCode((prev) => ({
        ...prev,
        [key]: value,
      }));

      focusNextInput(index);
    } else {
      setVerificationCode((prev) => ({
        ...prev,
        [key]: "",
      }));
    }
  };

  const combineVerificationCode = () => {
    const combinedCode = Object.values(verificationCode).join("");
    setCombinedVerification(combinedCode);
  };

  const checkCodeIsFiveDigits = () => {
    if (
      verificationCode.one &&
      verificationCode.two &&
      verificationCode.three &&
      verificationCode.four &&
      verificationCode.five
    ) {
      combineVerificationCode();
      setShake(false);
    }
  };

  useEffect(() => {
    checkCodeIsFiveDigits();
  }, [verificationCode]);

  useEffect(() => {
    // Check multiple sources for email to handle both scenarios:
    // 1. Free downloads registration flow - email in sessionStorage
    // 2. Full account login flow - email in value.email from login screen
    // 3. Parent registration flow - email in value.email from parent registration
    // 4. localStorage as fallback
    
    const pendingEmail = sessionStorage.getItem('pendingLoginEmail');
    const localEmail = localStorage.getItem("email");
    let emailToUse = null;

    // Priority: sessionStorage > value.email > localStorage
    if (pendingEmail && pendingEmail.trim()) {
      emailToUse = pendingEmail.trim();
      // Don't clear sessionStorage here - keep it for UpdateFreeDownloadInd call
      // It will be cleared when navigating away after verification
    } else if (value.email && value.email.trim()) {
      emailToUse = value.email.trim();
    } else if (localEmail) {
      // Try to parse if it's JSON stringified
      try {
        const parsed = JSON.parse(localEmail);
        emailToUse = typeof parsed === 'string' ? parsed.trim() : localEmail.trim();
      } catch (e) {
        emailToUse = localEmail.trim();
      }
    }

    // Set email in context if we found one and it's not already set or different
    if (emailToUse && emailToUse !== value.email) {
      setValue((prevValue) => ({
        ...prevValue,
        email: emailToUse,
      }));
    }

    // Store in localStorage for persistence (only if we have a valid email)
    if (emailToUse && emailToUse.trim()) {
      localStorage.setItem("email", emailToUse);
    }

    // Set loading to false once email is resolved
    if (emailToUse) {
      setLoading(false);
    }
  }, [setValue, value.email]);

  const verifyEmail = async () => {
    try {
      // Get email from value.email or fallback to sessionStorage/localStorage if needed
      const emailToUse = value.email || sessionStorage.getItem('pendingLoginEmail') || localStorage.getItem('email');
      
      if (!emailToUse) {
        return;
      }

      const response = await fetch(
        `${prefix}/api40/Registration/GetVerifiedEmail?Email=${emailToUse}`,
        {
          headers: {
            'X-API-Key': '8dnd6%3258%&k+_FsaTK25SCBNE58*t9'
          }
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch");
      }
      const data = await response.json();
      // Check both uppercase and lowercase property names for compatibility
      const verified = getProperty(data[0] || data, 'verified') || data[0]?.Verified || data[0]?.verified || data?.Verified || data?.verified;
      if (data && data[0] && verified === "Y") {
        setVerified(true);
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    // Check if this is a free downloads registration flow
    const isFreeDownloads = sessionStorage.getItem('isFreeDownloadsRegistration') === 'true';
    setIsFreeDownloadsRegistration(isFreeDownloads);
  }, []);

  useEffect(() => {
    if (verified) {
      if (isFreeDownloadsRegistration) {
        trackFreeDownloadSignupStep("email_verification", 3);
        trackFreeDownloadSignupComplete();
      }

      const timer = setTimeout(() => {
        // Clear sessionStorage items before navigating
        sessionStorage.removeItem('pendingLoginEmail');
        if (isFreeDownloadsRegistration) {
          // Clear the flag and navigate to free downloads page
          sessionStorage.removeItem('isFreeDownloadsRegistration');
          navigate("/freeDownloadsPapers");
        } else {
          // Regular flow - navigate to login screen
          navigate("/loginScreen");
        }
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [verified, navigate, isFreeDownloadsRegistration]);

  const resendVerificationCode = async () => {
    setResendLoad(true);
    try {
      // Get email from value.email or fallback to sessionStorage/localStorage if needed
      const emailToUse = value.email || sessionStorage.getItem('pendingLoginEmail') || localStorage.getItem('email');
      
      if (!emailToUse) {
        setResendLoad(false);
        return;
      }

      const response = await fetch(
        `${prefix}/api40/Registration/EmailVerifcationCode?Email=${emailToUse}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9"
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch");
      }

      if (response.status === 200) {
        setResendLoad(false);
        setShowTick(true);

        setTimeout(() => {
          setShowTick(false);
        }, 5000);
      }
    } catch (error) {
      setResendLoad(false);
    }
  };

  const checkVerificationCode = async () => {
    try {
      // Check if this is a free downloads registration flow
      const isFreeDownloads = sessionStorage.getItem('isFreeDownloadsRegistration') === 'true';
      
      // Get email from value.email (for full account) or sessionStorage (for free downloads)
      // For full account registrations, always use value.email first (from parent registration)
      let emailToUse;
      if (isFreeDownloads) {
        // Free downloads flow - use sessionStorage email
        emailToUse = sessionStorage.getItem('pendingLoginEmail') || value.email || localStorage.getItem('email');
      } else {
        // Full account flow - use value.email from parent registration (not sessionStorage)
        emailToUse = value.email || localStorage.getItem('email');
        // Clear any pendingLoginEmail from previous free downloads flow
        if (sessionStorage.getItem('pendingLoginEmail')) {
          sessionStorage.removeItem('pendingLoginEmail');
        }
      }
      
      if (!emailToUse) {
        setErrorMessage("Email not found. Please try again.");
        return;
      }

      // Both flows use UpdateFreeDownloadInd with the correct email
      const response = await fetch(
        `${prefix}/api40/Registration/UpdateFreeDownloadInd?Code=${combinedVerification}&Email=${emailToUse}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": "8dnd6%3258%&k+_FsaTK25SCBNE58*t9"
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch");
      }
      const data = await response.json();
      // Check both uppercase and lowercase property names for compatibility
      const verified = getProperty(data[0] || data, 'verified') || data[0]?.Verified || data[0]?.verified || data?.Verified || data?.verified;
      if (data && data[0] && verified === "NotUpdated") {
        setErrorMessage("Incorrect code please try again");
      } else {
        await verifyEmail();
        setErrorMessage("");
      }
    } catch (error) {
      setErrorMessage("An error occurred. Please try again.");
    }
  };

  return (
    <>
      <Helmet>
        <title>Email Verification | KSOL</title>
        <meta name="description" content="Verify your email address to access your KSOL account and free 11+ practice materials." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="w-full min-h-screen flex flex-col items-center justify-start bg-image p-4">
      <div className="w-full max-w-[80%] flex flex-col items-center p-6 xl:p-8">
        <img
          className={sessionCourseId ? "w-4/12 mb-4 " : "w-3/12 mb-4 "}
          src={sessionCourseId ? gcseLogo : logo}
        />
        {!verified && (
          <>
            <div className=" h-full w-full p-4  background-colour-not-verified flex flex-col items-center justify-center drop-shadow-lg md:w-3/5 md:h-auto">
              <h2 className="mt-4 text-center text-white">
                Please verify your email
              </h2>
              <p className="mt-4 text-center text-white">
                Enter the verification code sent to your email below
              </p>

              <div
                className={`flex justify-center gap-4 w-4/5 mt-4 mb-4 ${
                  errorMessage && verificationCode ? "horizontal-shaking" : ""
                }`}
              >
                {Object.keys(verificationCode).map((key, index) => (
                  <CustomInput
                    key={index}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    isVerification={true}
                    inputType="text"
                    maxLength={1}
                    value={verificationCode[key]}
                    onChange={(e) => handleInputChange(key, e, index)}
                    inputRef={(el) => (inputsRef.current[index] = el)}
                  />
                ))}
              </div>
              {errorMessage && (
                <div className=" mb-4">
                  <p className="text-white">{errorMessage}</p>
                </div>
              )}
              <div className="flex justify-center items-center">
                <div className="flex-col">
                  <div className="flex justify-center">
                    <p
                      onClick={() => setShowCaptcha(true)}
                      className="text-white mb-4 underline text-center cursor-pointer"
                    >
                      Resend code
                    </p>
                    {resendLoad && (
                      <div className="ml-2 mb-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" aria-label="Loading Spinner"></div>
                      </div>
                    )}

                    {showTick && (
                      <img className="ml-4 mb-4 w-8 h-8" src={tick} />
                    )}
                  </div>
                  {showCaptcha && (
                    <div className="mb-4">
                      <ReCAPTCHA
                        sitekey="6LfZ8GQqAAAAAC7zXqVuNZYXO9iKzId467JrCdPq"
                        onChange={handleRecaptchaChange}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="mb-4">
                <CustomButton onClick={checkVerificationCode} text={"Verify"} />
              </div>
            </div>
          </>
        )}
        {verified && (
          <div className="w-3/5 h-auto p-4 text-white background-colour-verified flex flex-col justify-center items-center drop-shadow-lg ">
            <div className="flex flex-col items-center w-full">
              <h2 className="mt-4">Verified!</h2>
              <div className="flex gap-4 items-center">
                <p>{isFreeDownloadsRegistration ? 'Redirecting to free downloads...' : 'Please login once redirected'}</p>
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" aria-label="Loading Spinner"></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default VerificationScreen;
